import { Button, Input, Segmented, Switch, Tooltip, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { MetricSelect } from "../shared/MetricSelect";
import { aggOptionsFor, allowedAggsFromDropdown, coerceAgg } from "../shared/metricAggregations";
import { AGG_PREFIX, type RadarMetricEntry } from "./radarState";
import { ColorSwatchPicker } from "./RadarColorSwatchPicker";

const { Text } = Typography;

interface MetricRowProps {
  entry: RadarMetricEntry;
  index: number;
  metricDropdown: any[];
  canRemove: boolean;
  showSeriesControls?: boolean;
  seriesColor: string;
  getDefaultLabel: (metricId: string | null) => string;
  onChange: (patch: Partial<Omit<RadarMetricEntry, "id">>) => void;
  onRemove: () => void;
}

// showSeriesControls=true when metrics are the series lines (vertex != "metric"):
//   adds color swatch, fill toggle, and name override input.
export function MetricRow({
  entry,
  index,
  metricDropdown,
  canRemove,
  showSeriesControls,
  seriesColor,
  getDefaultLabel,
  onChange,
  onRemove,
}: MetricRowProps) {
  const indent = 24;
  const allowedAggs = allowedAggsFromDropdown(metricDropdown, entry.metricId);
  const defaultLabel = entry.metricId
    ? `${AGG_PREFIX[entry.metricAgg]} ${getDefaultLabel(entry.metricId)}`
    : "";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "8px 10px",
        border: "1px solid #e0e5eb",
        borderRadius: 8,
        background: "#fafbfc",
      }}
    >
      {/* Picker row */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <Text style={{ fontSize: 11, color: "#888", minWidth: 18 }}>{index + 1}</Text>
        <MetricSelect
          metricDropdown={metricDropdown}
          value={entry.metricId}
          onChange={(v) =>
            // A helmet count has no best: the backend rejects it on save.
            onChange({ metricId: v, metricAgg: coerceAgg(entry.metricAgg, allowedAggsFromDropdown(metricDropdown, v)) })
          }
          style={{ flex: 1 }}
        />
        {canRemove && (
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
            style={{ flexShrink: 0 }}
          />
        )}
      </div>

      {entry.metricId != null && (
        <>
          {/* Agg */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: indent }}>
            <Text style={{ fontSize: 11, color: "#888" }}>Agg</Text>
            <Segmented
              size="small"
              options={aggOptionsFor(allowedAggs)}
              value={entry.metricAgg}
              onChange={(v) => onChange({ metricAgg: v as "avg" | "sum" | "best" })}
            />
          </div>

          {/* ACWR (GPS only) */}
          {entry.metricId.startsWith("gps-") && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: indent }}>
              <Tooltip title="Acute:Chronic Workload Ratio — 7-day load divided by 28-day load">
                <Text style={{ fontSize: 11, color: "#888", minWidth: 88, cursor: "default" }}>ACWR</Text>
              </Tooltip>
              <Switch
                size="small"
                checked={entry.metricIdentifierType === "acwr"}
                onChange={(v) => onChange({ metricIdentifierType: v ? "acwr" : "metric" })}
              />
            </div>
          )}

          {/* Fill + Color override (series step only) */}
          {showSeriesControls && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: indent }}>
                <Text style={{ fontSize: 11, color: "#888", minWidth: 88 }}>Fill</Text>
                <Switch
                  size="small"
                  checked={entry.fillArea ?? false}
                  onChange={(v) => onChange({ fillArea: v })}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: indent }}>
                <Text style={{ fontSize: 11, color: "#888", minWidth: 88 }}>Override color</Text>
                <Switch
                  size="small"
                  checked={!!entry.colorOverride}
                  onChange={(on) => onChange({ colorOverride: on ? seriesColor : undefined })}
                />
                {entry.colorOverride && (
                  <ColorSwatchPicker
                    color={entry.colorOverride}
                    onChange={(hex) => onChange({ colorOverride: hex })}
                  />
                )}
              </div>
            </>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: indent }}>
            <Text style={{ fontSize: 11, color: "#888", minWidth: 88 }}>Override label</Text>
            <Switch
              size="small"
              checked={entry.displayNameOverride !== undefined}
              onChange={(on) => onChange({ displayNameOverride: on ? defaultLabel : undefined })}
            />
            {entry.displayNameOverride !== undefined && (
              <Input
                size="small"
                placeholder={defaultLabel || "Label"}
                value={entry.displayNameOverride}
                onChange={(e) => onChange({ displayNameOverride: e.target.value })}
                style={{ flex: 1, fontSize: 11 }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
